/**
 * Canonical profile document types StatDoctor already collects.
 * Used to detect when hospital/email wording refers to an existing profile
 * upload — even if the label is phrased differently.
 *
 * Keep in sync with:
 * - Backend HospitalPreCredentialServiceImpl.buildDoctorFiles
 * - Backend ProfileDocumentFilter.java
 * - ai-shift-agent-email/profile_documents.py
 */

export type ProfileDocumentKey =
  | "resume"
  | "references"
  | "primaryDocument"
  | "otherDocument"
  | "threeDocument"
  | "policeCheck"
  | "criminalHistoryCheck"
  | "indemnity"
  | "workingWithChildrenCheck"
  | "vaccinationCertificate"
  | "medicareCard"
  | "medicalDegree"
  | "workVisa"
  | "approvalForSecondaryEmployment"
  | "ahpra";

type ProfileDocumentDefinition = {
  key: ProfileDocumentKey;
  /** Substrings matched on a normalized fingerprint (see normalizeFingerprint). */
  tokens: string[];
};

/** If any token matches, treat as a hospital-specific EXTRA (never auto-filter). */
export const HOSPITAL_EXTRA_EXCLUSIVE_TOKENS: string[] = [
  "vpd form",
  "tb questionnaire",
  "applicant declaration",
  "wet signature",
  "verification statement",
  "ndis worker screening",
  "medicare letter",
  "proda screenshot",
  "immigration notification",
  "als certificate",
  "apls certificate",
  "advanced life support",
  "advanced paediatric life support",
  "neonatal resus",
  "neonatal resuscitation",
  "neo resus",
  "cpd evidence",
  "cpd statement",
  "cpd activity",
  "cpd compliance",
  "continuing professional development",
  "training certificate",
  "hospital credentialing",
  "credentialing form",
  "orientation checklist",
  "vevo permission",
  "vevo check permission",
];

export const PROFILE_DOCUMENT_DEFINITIONS: ProfileDocumentDefinition[] = [
  {
    key: "resume",
    tokens: [
      "resume",
      "curriculum vitae",
      "curriculum",
      " cv ",
      "biography",
      "biographical",
    ],
  },
  {
    key: "references",
    tokens: [
      "referee",
      "referees",
      "referee report",
      "referee reports",
      "professional reference",
      "character reference",
      "reference report",
      "reference from",
      "references x",
      "minimum references",
      "line manager",
      "supervisor reference",
      "references requirement",
    ],
  },
  {
    key: "primaryDocument",
    tokens: [
      "primary id",
      "first id",
      "photo id",
      "proof of id",
      "proof of identity",
      "identity document",
      "passport",
      "driver licence",
      "drivers licence",
      "driver license",
      "drivers license",
      "100 points id",
    ],
  },
  {
    key: "otherDocument",
    tokens: ["secondary id", "second id", "other id", "other document"],
  },
  {
    key: "threeDocument",
    tokens: ["third id", "3rd id", "three id"],
  },
  {
    key: "policeCheck",
    tokens: [
      "police check",
      "police clearance",
      "national police check",
      "national police clearance",
      "npc",
      "afp check",
      "australian federal police",
    ],
  },
  {
    key: "criminalHistoryCheck",
    tokens: [
      "criminal history",
      "criminal record",
      "criminal record check",
      "national crime check",
    ],
  },
  {
    key: "indemnity",
    tokens: [
      "indemnity",
      "professional indemnity",
      "medical indemnity",
      "malpractice insurance",
      "pi insurance",
      "practitioner insurance",
    ],
  },
  {
    key: "workingWithChildrenCheck",
    tokens: [
      "wwcc",
      "working with children",
      "working with children check",
      "blue card",
      "yellow card",
      "ochre card",
    ],
  },
  {
    key: "vaccinationCertificate",
    tokens: [
      "vaccination",
      "vaccination certificate",
      "vaccination evidence",
      "immunisation",
      "immunization",
      "immunisation evidence",
      "immunization evidence",
      "mandatory vaccination",
      "flu vaccination",
      "hepatitis b",
      "covid vaccination",
      "covid vax",
    ],
  },
  {
    key: "medicareCard",
    tokens: ["medicare card", "medicare number", "medicare"],
  },
  {
    key: "medicalDegree",
    tokens: [
      "medical degree",
      "university degree",
      "base degree",
      "qualification certificate",
      "evidence of degree",
      "evidence of fellowship",
      "fellowship certificate",
      "fellowship credentials",
      "academic transcript",
      "degree certificate",
      "credentials academic",
      "medical qualification",
      "specialist qualification",
      "gesa",
    ],
  },
  {
    key: "workVisa",
    tokens: ["work visa", "right to work", "visa status", "work rights"],
  },
  {
    key: "approvalForSecondaryEmployment",
    tokens: [
      "secondary employment",
      "approval for secondary employment",
      "locum approval",
    ],
  },
  {
    key: "ahpra",
    tokens: ["ahpra", "medical registration", "provider number"],
  },
];

/** Normalize free-text labels for token/substring matching. */
export const normalizeDocumentFingerprint = (text: string): string => {
  const collapsed = String(text || "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();
  return collapsed ? ` ${collapsed} ` : "";
};

export const fingerprintContainsToken = (
  fingerprint: string,
  token: string,
): boolean => {
  const normalizedToken = String(token || "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();
  if (!normalizedToken) return false;
  return fingerprint.includes(` ${normalizedToken} `);
};

export const isHospitalExtraExclusiveLabel = (label: string): boolean => {
  const fp = normalizeDocumentFingerprint(label);
  if (!fp.trim()) return false;
  return HOSPITAL_EXTRA_EXCLUSIVE_TOKENS.some((token) =>
    fingerprintContainsToken(fp, token),
  );
};

export const resolveProfileDocumentKey = (
  label: string,
): ProfileDocumentKey | null => {
  const text = String(label || "").trim();
  if (!text) return null;
  if (isHospitalExtraExclusiveLabel(text)) return null;

  const fp = normalizeDocumentFingerprint(text);
  if (!fp.trim()) return null;

  for (const def of PROFILE_DOCUMENT_DEFINITIONS) {
    if (def.tokens.some((token) => fingerprintContainsToken(fp, token))) {
      return def.key;
    }
  }

  return null;
};

export const isProfileDocumentLabel = (label: string): boolean =>
  resolveProfileDocumentKey(label) !== null;
